/**
 * Routing Configuration
 * 支援三種解析度: Desktop (>1200px), Tablet (768-1200px), Mobile (<768px)
 */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Spin } from 'antd';

// Layouts
import MainLayout from '@/components/layouts/MainLayout';
import AuthLayout from '@/components/layouts/AuthLayout';
import PDDLayout from '@/components/layouts/PDDLayout';
import ProtectedRoute from '@/components/ProtectedRoute';

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/RegisterPage'));

// CDM-PDD v12.0 Pages
const PDDIndexPage = lazy(() => import('@/features/projects/pdd/PDDIndexPage'));
const BasicInformationPage = lazy(() => import('@/features/projects/pdd/BasicInformationPage'));
const A6HistoryPage = lazy(() => import('@/features/projects/pdd/SectionA/A6HistoryPage'));
const B5AdditionalityPage = lazy(() => import('@/features/projects/pdd/SectionB/B5AdditionalityPage'));
const E1ModalitiesPage = lazy(() => import('@/features/projects/pdd/SectionE/E1ModalitiesPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const ProjectListPage = lazy(() => import('@/features/projects/ProjectListPage'));
const ProjectsBasicManagementPage = lazy(() => import('@/features/projects/ProjectsBasicManagementPage'));
const PADescriptionPage = lazy(() => import('@/features/projects/PADescriptionPage'));
const ProjectDetailPage = lazy(() => import('@/features/projects/ProjectDetailPage'));
const SectionBPage = lazy(() => import('@/features/projects/SectionBPage'));
const SectionCPage = lazy(() => import('@/features/projects/SectionCPage'));
const SectionDPage = lazy(() => import('@/features/projects/SectionDPage'));
const SectionEPage = lazy(() => import('@/features/projects/SectionEPage'));
const SectionFPage = lazy(() => import('@/features/projects/SectionFPage'));
const ProjectBaselinePage = lazy(() => import('@/features/projects/ProjectBaselinePage'));
const EmissionReductionPage = lazy(() => import('@/features/projects/EmissionReductionPage'));
const ProjectEmissionsPage = lazy(() => import('@/features/projects/ProjectEmissionsPage'));
const ProjectSupportPage = lazy(() => import('@/features/projects/ProjectSupportPage'));
const AppendixPage = lazy(() => import('@/features/projects/AppendixPage'));
const ProjectWorkflowPage = lazy(() => import('@/features/projects/ProjectWorkflowPage'));
const OrganizationListPage = lazy(() => import('@/features/organizations/OrganizationListPage'));
const OrganizationDetailPage = lazy(() => import('@/features/organizations/OrganizationDetailPage'));
const OrganizationStatsPage = lazy(() => import('@/features/organizations/OrganizationStatsPage'));
const UserListPage = lazy(() => import('@/features/users/UserListPage'));
const UserDetailPage = lazy(() => import('@/features/users/UserDetailPage'));
const MonitoringPage = lazy(() => import('@/features/monitoring/MonitoringPage'));
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const ReportManagerPage = lazy(() => import('@/features/reports/ReportManagerPage'));
const ReportWizardPage = lazy(() => import('@/features/reports/ReportWizardPage'));
const ReportPreviewPage = lazy(() => import('@/features/reports/ReportPreviewPage'));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'));
const SystemCodePage = lazy(() => import('@/features/settings/SystemCodePage'));
const GHGTablesPage = lazy(() => import('@/features/settings/GHGTablesPage'));
const BoundaryEmissionsPage = lazy(() => import('@/features/settings/BoundaryEmissionsPage'));
const MethodologyConfigPage = lazy(() => import('@/features/settings/MethodologyConfigPage'));
const MethodologyExplanationPage = lazy(() => import('@/features/settings/MethodologyExplanationPage'));
const SectoralMethodologyRelManagementPage = lazy(() => import('@/features/settings/SectoralMethodologyRelManagementPage'));
const NotFoundPage = lazy(() => import('@/features/common/NotFoundPage'));
const InDevelopmentPage = lazy(() => import('@/features/common/InDevelopmentPage'));

// Loading component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    gap: '16px'
  }}>
    <Spin size="large" />
    <div>載入中...</div>
  </div>
);

// Suspense wrapper
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType<any>>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      {
        path: 'login',
        element: withSuspense(LoginPage),
      },
      {
        path: 'register',
        element: withSuspense(RegisterPage),
      },
    ],
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'dashboard',
        element: withSuspense(DashboardPage),
      },
      {
        path: 'projects',
        element: withSuspense(ProjectListPage),
      },
      {
        path: 'projects/basic-management',
        element: withSuspense(ProjectsBasicManagementPage),
      },
      {
        path: 'projects/pa-description',
        element: withSuspense(PADescriptionPage),
      },
      {
        path: 'projects/baseline',
        element: withSuspense(ProjectBaselinePage),
      },
      {
        path: 'projects/emission-reduction',
        element: withSuspense(EmissionReductionPage),
      },
      {
        path: 'projects/project-emissions',
        element: withSuspense(ProjectEmissionsPage),
      },
      {
        path: 'projects/project-support',
        element: withSuspense(ProjectSupportPage),
      },
      {
        path: 'projects/appendix',
        element: withSuspense(AppendixPage),
      },
      {
        path: 'projects/workflow',
        element: withSuspense(ProjectWorkflowPage),
      },
      {
        path: 'projects/:projectId',
        element: withSuspense(ProjectDetailPage),
      },
      {
        path: 'projects/section-b',
        element: withSuspense(SectionBPage),
      },
      {
        path: 'projects/section-c',
        element: withSuspense(SectionCPage),
      },
      {
        path: 'projects/section-d',
        element: withSuspense(SectionDPage),
      },
      {
        path: 'projects/section-e',
        element: withSuspense(SectionEPage),
      },
      {
        path: 'projects/section-f',
        element: withSuspense(SectionFPage),
      },
      // CDM-PDD v12.0 Routes
      {
        path: 'projects/:projectId/pdd',
        element: (
          <ProtectedRoute>
            <PDDLayout />
          </ProtectedRoute>
        ),
        children: [
          // PDD Index
          {
            index: true,
            element: withSuspense(PDDIndexPage),
          },
          // Basic Information (保留舊的，兼容性)
          {
            path: 'basic',
            element: withSuspense(BasicInformationPage),
          },
          // Section A (保留舊的，兼容性)
          {
            path: 'section-a/history',
            element: withSuspense(A6HistoryPage),
          },
          // Section B
          {
            path: 'section-b/additionality',
            element: withSuspense(B5AdditionalityPage),
          },
          // Section E
          {
            path: 'section-e/modalities',
            element: withSuspense(E1ModalitiesPage),
          },
        ],
      },
      {
        path: 'organizations',
        element: withSuspense(OrganizationListPage),
      },
      {
        path: 'organizations/new',
        element: withSuspense(OrganizationDetailPage),
      },
      {
        path: 'organizations/:id',
        element: withSuspense(OrganizationDetailPage),
      },
      {
        path: 'organizations/:id/edit',
        element: withSuspense(OrganizationDetailPage),
      },
      {
        path: 'organizations/:id/stats',
        element: withSuspense(OrganizationStatsPage),
      },
      {
        path: 'users',
        element: withSuspense(UserListPage),
      },
      {
        path: 'users/new',
        element: withSuspense(UserDetailPage),
      },
      {
        path: 'users/:id',
        element: withSuspense(UserDetailPage),
      },
      {
        path: 'users/:id/edit',
        element: withSuspense(UserDetailPage),
      },
      {
        path: 'monitoring',
        element: withSuspense(MonitoringPage),
      },
      {
        path: 'monitoring/info',
        element: withSuspense(InDevelopmentPage),
      },
      {
        path: 'monitoring/section-a',
        element: withSuspense(InDevelopmentPage),
      },
      {
        path: 'monitoring/section-b',
        element: withSuspense(InDevelopmentPage),
      },
      {
        path: 'monitoring/section-c',
        element: withSuspense(InDevelopmentPage),
      },
      {
        path: 'monitoring/section-d',
        element: withSuspense(InDevelopmentPage),
      },
      {
        path: 'monitoring/section-e',
        element: withSuspense(InDevelopmentPage),
      },
      {
        path: 'reports',
        element: withSuspense(ReportManagerPage),
      },
      {
        path: 'monitoring-reports',
        element: withSuspense(InDevelopmentPage),
      },
      {
        path: 'reports/wizard',
        element: withSuspense(ReportWizardPage),
      },
      {
        path: 'settings',
        element: withSuspense(SettingsPage),
      },
      {
        path: 'settings/system-codes',
        element: withSuspense(SystemCodePage),
      },
      {
        path: 'settings/ghg-tables',
        element: withSuspense(GHGTablesPage),
      },
      {
        path: 'settings/boundary-emissions',
        element: withSuspense(BoundaryEmissionsPage),
      },
      {
        path: 'settings/methodology-config',
        element: withSuspense(MethodologyConfigPage),
      },
      {
        path: 'settings/methodology-explanation',
        element: withSuspense(MethodologyExplanationPage),
      },
      {
        path: 'settings/sectoral-methodology-rel',
        element: withSuspense(SectoralMethodologyRelManagementPage),
      },
    ],
  },
  // Standalone Report Preview (without MainLayout)
  {
    path: '/report-preview/:projectId',
    element: (
      <ProtectedRoute>
        {withSuspense(ReportPreviewPage)}
      </ProtectedRoute>
    ),
  },
  {
    path: '*',
    element: withSuspense(NotFoundPage),
  },
]);

export default router;
